import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Records the arbitrator's dictation and appends the transcript to the order text. */
export const DictateButton = ({ onTranscript, testId }) => {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);

  useEffect(() => () => {
    clearInterval(timer.current);
    recorder.current?.stream?.getTracks?.().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return toast.error("This browser does not support recording");
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current);
        const blob = new Blob(chunks.current, { type: mime || "audio/webm" });
        if (blob.size < 1200) {
          setState("idle");
          return toast.error("Recording too short — hold and dictate for a moment");
        }
        setState("transcribing");
        const fd = new FormData();
        fd.append("file", blob, "dictation.webm");
        try {
          const { data } = await api.post("/dictate", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data.text) {
            onTranscript(data.text);
            toast.success("Dictation transcribed");
          } else {
            toast.error("Nothing could be made out of the recording");
          }
        } catch (e) {
          toast.error(errMsg(e));
        } finally {
          setState("idle");
          setSeconds(0);
        }
      };
      rec.start();
      recorder.current = rec;
      setState("recording");
      setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= 180) stop();
        return s + 1;
      }), 1000);
    } catch (e) {
      toast.error("Microphone permission is required to dictate");
    }
  };

  const stop = () => recorder.current?.state === "recording" && recorder.current.stop();

  if (state === "transcribing") {
    return (
      <Button type="button" disabled data-testid={`${testId}-transcribing`}
        className="rounded-none bg-slate-900 text-white uppercase tracking-widest text-xs h-10">
        <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Transcribing…
      </Button>
    );
  }

  return (
    <Button type="button" data-testid={state === "recording" ? `${testId}-stop` : testId}
      onClick={state === "recording" ? stop : start}
      className={`rounded-none uppercase tracking-widest text-xs h-10 text-white transition-colors duration-200 ${
        state === "recording" ? "bg-red-800 hover:bg-red-700 animate-pulse" : "bg-slate-900 hover:bg-slate-800"
      }`}>
      {state === "recording" ? (
        <><Square className="h-3.5 w-3.5 mr-2" /> Stop {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</>
      ) : (
        <><Mic className="h-3.5 w-3.5 mr-2" /> Dictate</>
      )}
    </Button>
  );
};

export default DictateButton;
